import type { Collection, CollectionId } from '@/scopes/search/search/infrastructure/api/types';
import { css, styled, type RuleSet } from 'styled-components';

import { BulletList, Icon, SpaceBetween, Typography, tk } from '@sunwebgroup/ui-components';

import { useIntl } from '@/intl';

import { LazyImage } from '@/scopes/common/components/Image';

interface CollectionBannerProps {
  collection: Collection;
  description: string;
  image: string;
  title: string;
  usps: string[];
}

export function CollectionBanner({ collection, description, image, title, usps }: CollectionBannerProps) {
  const intl = useIntl();

  const collectionName = (collectionId: CollectionId) => {
    const translationIds = {
      atlantis: 'collection_name_atlantis',
      boutique: 'collection_name_boutique',
      cycling: 'collection_name_cycling',
      excellent: 'collection_name_excellent',
      famfun: 'collection_name_famfun',
      primavera: 'collection_name_primavera',
      secrets: 'collection_name_secrets',
      selections: 'collection_name_selections',
      totally_snow: 'collection_name_totally_snow',
      villages: 'collection_name_villages',
      xperience: 'collection_name_xperience',
    };
    const translationId = collectionId in translationIds ? translationIds[collectionId] : 'collection_name_unknown';

    return intl.formatMessage({ id: translationId });
  };

  const collectionIcon = (collectionId: CollectionId) => {
    if (collectionId === 'primavera') {
      return 'IconSparkPrimavera';
    }
    return 'IconSparkSunweb';
  };

  return (
    <Container $id={collection.id}>
      <Badge direction="y" spacing={tk('spacing_2xs')} alignItems="center" justifyContent="center">
        <Icon glyph={collectionIcon(collection.id)} size="lg" />
        <Typography $variant="body-xs" as="span">
          {collection.text || collectionName(collection.id)}
        </Typography>
      </Badge>
      <LazyImageWrapper>
        <LazyImage
          src={image}
          alt={title}
          fallbackText={intl.formatMessage({
            id: 'failed_to_load_image',
          })}
        />
      </LazyImageWrapper>
      <Content direction="y" spacing={tk('spacing_lg')} padding={tk('spacing_lg')}>
        <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
          <Typography $variant="heading-md" as="h4">
            {title}
          </Typography>
          <Typography $variant="body-sm" as="p">
            {description}
          </Typography>
        </SpaceBetween>
        <BulletList.Root $size="small" $variant="multiline">
          {usps.map((usp, index) => (
            <BulletList.Item key={index}>{usp}</BulletList.Item>
          ))}
        </BulletList.Root>
      </Content>
    </Container>
  );
}

CollectionBanner.displayName = 'CollectionBanner';

const Container = styled.div<{ $id: CollectionId }>`
  overflow: hidden;
  box-shadow: ${tk('shadow_md_bottom')};
  border-radius: ${tk('radius_large')};

  @media ${tk('mq_tablet')} {
    display: flex;
  }

  ${({ $id }) => modCollectionColor($id)}
`;

const Badge = styled(SpaceBetween)`
  position: absolute;
  z-index: 1;
  margin: ${tk('spacing_sm')};
  border-radius: ${tk('radius_large')};
  width: 72px;
  height: 72px;
  padding: ${tk('spacing_sm')} 0;
`;

const LazyImageWrapper = styled.div`
  width: 100%;
  height: 180px;

  @media ${tk('mq_tablet')} {
    width: 255px;
    height: auto;
  }

  @media ${tk('mq_desktop_sm')} {
    width: 375px;
  }

  background-color: ${tk('color_palette_grey_100')};
`;

const Content = styled(SpaceBetween)`
  flex: 1;
  padding-bottom: ${tk('spacing_xl')};
`;

const modCollectionColor = (id: CollectionId): RuleSet<object> => {
  const backgroundColors = {
    atlantis: tk('color_collection_atlantis'),
    boutique: tk('color_collection_boutique'),
    cycling: tk('color_collection_cycling'),
    excellent: tk('color_collection_excellent'),
    famfun: tk('color_collection_fam_fun'),
    primavera: tk('color_collection_primavera'),
    secrets: tk('color_collection_secrets'),
    selections: tk('color_collection_selections'),
    totally_snow: tk('color_collection_totally'),
    villages: tk('color_collection_villages'),
    xperience: tk('color_collection_xperience'),
  };

  return css`
    ${Badge},
    ${Content} {
      background-color: ${id in backgroundColors ? backgroundColors[id] : tk('color_semantic_bg')};
      color: ${id in backgroundColors ? tk('color_semantic_text_on_color') : tk('color_semantic_text')};
    }
  `;
};
